import { Person } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  ButtonBase,
  Card,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Theme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { WalletCreatedData, walletEvents } from '../../../events/walletEvents';
import useWallets from '../../../hooks/useWallets';
import { Wallet as FormWallet } from '../../forms/basic-transactions/components/types';
import BaseDialog from '../../ui/BaseDialog';
import DrawerBottom from '../../ui/DrawerBottom/DrawerBottom';
import { CheckIcon } from '../../ui/icons';
import ButtonCreateWallet from '../Wallets/ButtonCreateWallet';
import BaseWalletItem from '../Wallets/BaseWalletItem';
import { useAuth } from '../../../hooks/useAuth';
import InlineWalletCreator from './InlineWalletCreator';

// Helper function to deduplicate wallets by id
const deduplicateWallets = (wallets: Wallet[]): Wallet[] => {
  const seen = new Set<string>();
  return wallets.filter(wallet => {
    if (seen.has(wallet.id)) return false;
    seen.add(wallet.id);
    return true;
  });
};

interface WalletSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectWallet?: (wallet: any) => void;
  onSelectWallets?: (wallets: any[]) => void;
  multipleSelection?: boolean;
  selectSourceWallet?: boolean;
  selectDestinationWallet?: boolean;
  walletTypes?: WalletType[];
  personalWallet?: boolean;
  hideSearchField?: boolean;
  transactionType?: 'expense' | 'income' | 'transfer' | 'loan' | 'credit_card';
  loanType?: string | null;
  excludeWalletIds?: string[];
  customTitle?: string;
}

interface WalletItemProps {
  wallet: Wallet;
  group: WalletGroup;
  showCheckbox?: boolean;
  checked?: boolean;
  highlight?: boolean;
  onClick: (group: WalletGroup, wallet: Wallet) => void;
}

const WalletItem = ({
  wallet,
  group,
  onClick,
  showCheckbox,
  checked = false,
  highlight = false,
}: WalletItemProps) => {
  const [isChecked, setIsChecked] = useState(checked);

  const onClickHandler = (w: Wallet) => {
    if (showCheckbox) {
      setIsChecked(!isChecked);
    }
    onClick(group, w);
  };

  return (
    <BaseWalletItem
      wallet={wallet}
      onClick={onClickHandler}
      selected={isChecked}
      showCheckbox={showCheckbox}
      highlight={highlight}
    />
  );
};


interface PersonalWalletProps {
  showCheckbox?: boolean;
  checked?: boolean;
  onClick: (group: WalletGroup, wallet: Wallet) => void;
}

const PersonalWallet = ({
  showCheckbox,
  checked,
  onClick,
}: PersonalWalletProps) => {
  const { t } = useTranslation(['common']);
  const { user } = useAuth();
  const username = user?.username || '';
  const walletName = username ? `${username} (${t('myself', { ns: 'common' })})` : t('myself', { ns: 'common' });
  const wallet = {
    id: 'personal-wallet',
    name: walletName,
    balance: 0,
    type: 'BASIC',
  } as Wallet;
  const theme: Theme = useTheme();
  const [isChecked, setIsChecked] = useState(checked);
  const onClickHandler = () => {
    if (showCheckbox) {
      setIsChecked(!isChecked);
    }
    onClick({
      id: 'personal-wallet',
      title: '',
      hasInfo: false,
      wallets: []
    }, wallet);
  };
  return (
    <Card variant='outlined' sx={{ width: '100%', marginBottom: theme.spacing(1) }}>
      <ButtonBase
        sx={{
          py: 1,
          px: 2,
          width: '100%',
          textAlign: 'left',
          backgroundColor: isChecked ? theme.palette.teal[50] : 'transparent',
        }}
        onClick={() => onClickHandler()}
      >
        <Stack
          useFlexGap
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '50%',
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.grey[300],
              }}
            >
              <Person sx={{ fontSize: 18 }} />
            </Card>
            <Typography variant="body1">{wallet.name}</Typography>
          </Stack>
          {showCheckbox && (
            <Card
              elevation={0}
              sx={{
                borderRadius: 0.5,
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isChecked ? theme.palette.primary.main : 'transparent',
                border: isChecked
                  ? 'none'
                  : `2px solid ${theme.palette.grey[300]}`,
                color: theme.palette.primary.contrastText,
              }}
            >
              {isChecked && <CheckIcon />}
            </Card>
          )}
        </Stack>
      </ButtonBase>
    </Card>
  );
};

interface WalletGroupProps {
  group: WalletGroup;
  index: number;
  highlightedWalletId?: string | null;
  onSelectWallet: (group: WalletGroup, wallet: Wallet) => void;
}

const WalletsGroup = ({ group, index, onSelectWallet, highlightedWalletId }: WalletGroupProps) => {
  const theme: Theme = useTheme();
  return (
    <Accordion key={index} defaultExpanded sx={{ boxShadow: 'none' }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-${index}-content`}
        id={`panel-${index}-header`}
        sx={{
          px: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h4">{group.title}</Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          '&.MuiAccordionDetails-root': {
            padding: 0,
            pt: 2,
          },
        }}
      >
        {deduplicateWallets(group.wallets).map((wallet) => (
          <WalletItem
            key={wallet.id}
            wallet={wallet}
            group={group}
            onClick={onSelectWallet}
            highlight={wallet.id === highlightedWalletId}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const WalletSelector = ({
  open,
  onClose,
  onSelectWallet,
  onSelectWallets,
  selectSourceWallet = true,
  selectDestinationWallet = false,
  walletTypes,
  multipleSelection = false,
  personalWallet = false,
  hideSearchField = false,
  transactionType,
  loanType,
  excludeWalletIds = [],
  customTitle,
}: WalletSelectorProps) => {
  const theme: Theme = useTheme();
  const { t } = useTranslation(['common', 'assets', 'transactions']);
  const { walletGroups, mutate } = useWallets();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [wallets, setWallets] = useState<FormWallet[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInlineCreator, setShowInlineCreator] = useState(false);
  const [highlightedWalletId, setHighlightedWalletId] = useState<string | null>(null);

  // Check if this is a loan-only wallet selector
  const isLoanWalletOnly = (walletTypes?.length === 1 && walletTypes[0] === 'LOAN') ||
    (transactionType === 'loan' && loanType === 'borrow-expense');

  useEffect(() => {
    if (!open) return;

    const sourceWalletName = location.state?.transaction?.transfer?.sourceWallet;
    const destinationWalletName = location.state?.transaction?.transfer?.destinationWallet;
    const walletId = location.state?.transaction?.walletId;

    if (selectSourceWallet && sourceWalletName) {
      findAndSelectWallet(sourceWalletName, 'name');
    } else if (selectDestinationWallet && destinationWalletName) {
      findAndSelectWallet(destinationWalletName, 'name');
    } else if (selectSourceWallet && walletId) {
      findAndSelectWallet(walletId, 'id');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    location.state?.transaction,
    walletGroups,
    selectSourceWallet,
    selectDestinationWallet,
  ]);

  useEffect(() => {
    const unsubscribe = walletEvents.subscribe('wallet:created', (_data: WalletCreatedData) => {
      mutate();
    });

    return () => unsubscribe();
  }, [mutate]);

  // Reset state when dialog is closed
  useEffect(() => {
    if (!open) {
      setShowInlineCreator(false);
      setHighlightedWalletId(null);
    }
  }, [open]);

  const handleInlineCreatorSuccess = (walletData: any) => {
    setShowInlineCreator(false);
    setHighlightedWalletId(walletData?.wallet?.id || null);
    setTimeout(() => setHighlightedWalletId(null), 3000);
  };

  const handleInlineCreatorCancel = () => {
    setShowInlineCreator(false);
  };

  const findAndSelectWallet = (value: string, key: 'name' | 'id') => {
    for (const group of walletGroups || []) {
      const matchedWallet = group.wallets.find(
        (wallet) => wallet[key] === value
      );
      if (matchedWallet) {
        handleSelectWallet(group, matchedWallet);
        break;
      }
    }
  };

  const handleSelectWallet = (group: any, wallet: Wallet) => {
    const selectedWallet = {
      id: group?.id,
      title: group?.title,
      wallet: {
        id: wallet.id,
        name: wallet.name,
        balance: wallet.balance,
        type: wallet.type,
      },
    };
    if (multipleSelection) {
      setWallets((prev) => {
        const isAlreadySelected = prev.some((w) => w.wallet.id === wallet.id);
        if (isAlreadySelected) {
          return prev.filter((w) => w.wallet.id !== wallet.id);
        }
        if (selectedWallet.id === 'personal-wallet') {
          return [selectedWallet, ...prev];
        }
        return [...prev, selectedWallet];
      });
      return;
    }
    onSelectWallet?.(selectedWallet);
    onClose();
  };

  const handleSubmitMultipleSelection = () => {
    if (multipleSelection && onSelectWallets) {
      onSelectWallets(wallets);
    }
    onClose();
  };

  let title = customTitle || t('select_wallet', { ns: 'transactions' });


  const filteredWalletGroups = walletGroups
    ?.sort((a, b) => (a.id === 'basic' ? -1 : 1))
    .filter((group) => {
      // For borrow-expense, only show loan wallet group
      if (transactionType === 'loan' && loanType === 'borrow-expense') {
        return group.id === 'loan';
      }
      // For expense, income, borrow, lend, repay - only show basic and credit wallet groups
      const showBasicAndCreditOnly =
        transactionType === 'expense' ||
        transactionType === 'income' ||
        (transactionType === 'loan' && ['borrow', 'lend', 'repay'].includes(loanType || ''));
      if (showBasicAndCreditOnly) {
        return group.id === 'basic' || group.id === 'credit';
      }
      if (Array.isArray(walletTypes) && walletTypes.length > 0) {
        return group.wallets.some((wallet) =>
          walletTypes.includes(wallet.type.toUpperCase() as WalletType)
        );
      }
      return group.wallets.length > 0;
    })
    .map(group => ({
      ...group,
      wallets: group.wallets.filter(wallet =>
        wallet.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !excludeWalletIds.includes(wallet.id)
      )
    }))
    .filter(group => group.wallets.length > 0);

  const searchField = hideSearchField ? <></> : (
    <TextField
      fullWidth
      variant="outlined"
      value={searchTerm}
      placeholder={t('search_wallet', { ns: 'transactions', defaultValue: 'Tìm kiếm ví...' })}
      onChange={(e) => setSearchTerm(e.target.value)}
      sx={{ mt: 0, mb: 2 }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        },
      }}
    />
  );

  let content: React.ReactNode = null;

  if (walletTypes?.length === 1 || isLoanWalletOnly) {
    const walletGroup = filteredWalletGroups[0];
    // Only use walletGroup title if no customTitle was provided
    if (!customTitle) {
      title = walletGroup?.title || title;
    }
    content = (
      <>
        {/* {searchField} */}
        {deduplicateWallets(walletGroup?.wallets || []).map((wallet) => (
          <WalletItem
            key={wallet.id}
            wallet={wallet}
            group={walletGroup}
            onClick={handleSelectWallet}
            showCheckbox={multipleSelection}
            checked={wallets.some((w) => w.wallet.id === wallet.id)}
            highlight={wallet.id === highlightedWalletId}
          />
        ))}
      </>
    );
  } else {
    content = (
      <>
        {/* {searchField} */}
        {filteredWalletGroups
          ?.map((group, index) => (
            <WalletsGroup
              key={index}
              group={group}
              index={index}
              onSelectWallet={handleSelectWallet}
              highlightedWalletId={highlightedWalletId}
            />
          ))}
      </>
    );
  }

  if (personalWallet) {
    content = (
      <>
        <PersonalWallet
          key={'personal-wallet'}
          showCheckbox={multipleSelection}
          checked={wallets.some((w) => w.wallet.id === 'personal-wallet')}
          onClick={handleSelectWallet}
        />
        {content}
      </>
    );
  }

  // Add inline wallet creator at the bottom for loan wallets
  if (isLoanWalletOnly && showInlineCreator) {
    content = (
      <>
        {content}
        <InlineWalletCreator
          onCancel={handleInlineCreatorCancel}
          onSuccess={handleInlineCreatorSuccess}
        />
      </>
    );
  }

  const submitButton = multipleSelection ? (
    <Button
      fullWidth
      variant="contained"
      size="large"
      onClick={handleSubmitMultipleSelection}
      disabled={!wallets.filter(w => w.id !== "personal-wallet").length}
    >
      {t('done', { ns: 'common' })}
    </Button>
  ) : null;

  content = (
    <>
      {searchField}
      {content}
    </>
  )

  // Use inline creator button for loan wallets, otherwise use the standard ButtonCreateWallet
  const createWalletButton = isLoanWalletOnly ? (
    <IconButton
      onClick={() => setShowInlineCreator(true)}
      sx={{
        color: theme.palette.text.primary,
      }}
    >
      <AddIcon />
    </IconButton>
  ) : (
    <ButtonCreateWallet
      hideText
      circleButton
      renderFormOnPopup
      textColor={theme.palette.text.primary}
    />
  );

  if (isDesktop) {
    return (
      <BaseDialog
        open={open}
        maxWidth='sm'
        onClose={onClose}
        title={title}
        extraAction={createWalletButton}
        dialogActions={submitButton}
      >
        {content}
      </BaseDialog>
    );
  }

  return (
    <DrawerBottom
      mobileKeyboardAware
      open={open}
      onClose={onClose}
      title={title}
      extraAction={createWalletButton}
      footer={submitButton}
    >
      {content}
    </DrawerBottom>
  );
};

export default WalletSelector;
